import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";

// Component creates a shadow root. It also copies our glboal css styles inside.
export function ShadowDomContainer({
  children,
  containerRef,
  ...props
}: {
  children: React.ReactNode;
  containerRef?: React.ForwardedRef<HTMLDivElement | null>;
} & React.HTMLProps<HTMLDivElement>) {
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | undefined>(undefined);
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    const stopPropagation = (e: Event) => {
      e.stopPropagation();
    };

    const conditionalStopPropagation = (e: Event) => {
      // Allow mouseup events to propagate to handle resize operations
      // that might have started outside the shadow DOM
      if (e.type === "mouseup") {
        return;
      }
      e.stopPropagation();
    };

    // Stop pointerdown in the capture phase to prevent clicks from leaking out
    // and closing modals on the host page.
    host.addEventListener("pointerdown", stopPropagation, true);

    // Stop other mouse events in the bubbling phase to ensure that React's event
    // handlers have a chance to run, but allow mouseup to propagate for resize operations
    const bubbleEvents: (keyof HTMLElementEventMap)[] = ["click", "mousedown", "mouseup"];
    bubbleEvents.forEach((eventName) => {
      host.addEventListener(eventName, conditionalStopPropagation, false);
    });

    return () => {
      host.removeEventListener("pointerdown", stopPropagation, true);
      bubbleEvents.forEach((eventName) => {
        host.removeEventListener(eventName, conditionalStopPropagation, false);
      });
    };
  }, []);

  return (
    <>
      <div
        ref={(el) => {
          hostRef.current = el;
          if (typeof containerRef === "function") {
            containerRef(el);
          } else if (containerRef) {
            containerRef.current = el;
          }

          if (el) {
            if (!el.shadowRoot) {
              el.attachShadow({ mode: "open" });
            }
            if (shadowRoot !== el.shadowRoot) {
              setShadowRoot(el.shadowRoot ?? undefined);
            }
          }
        }}
        {...props}
      ></div>
      {shadowRoot &&
        createPortal(
          <>
            <div
              data-pyne-css="shadow-container"
              ref={(el) => {
                if (el && el.children.length === 0) {
                  const styles = document.querySelectorAll("head > [data-pyne-css=host]");
                  styles.forEach((style) => {
                    el.appendChild(style.cloneNode(true));
                  });
                }
              }}
            />
            {children}
          </>,
          shadowRoot
        )}
    </>
  );
}
